'use client';

import React, { useEffect, useState } from 'react';
import { Server, ShieldCheck, CalendarDays, CircleDollarSign } from 'lucide-react';
import { DEFAULT_LOCALE, isLocale, Locale, translate } from '@/lib/i18n';

function getStoredLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const key = Object.keys(localStorage).find((k) => k.startsWith('xg_locale_'));
  const val = key ? localStorage.getItem(key) : null;
  return isLocale(val) ? val : DEFAULT_LOCALE;
}

export interface LoginSearchParams {
  error?: string;
  userId?: string;
  userRoles?: string;
  expectedOwner?: string;
  expectedDev?: string;
  expectedAdmin?: string;
}

export default function LoginContent({ searchParams }: { searchParams: LoginSearchParams }) {
  const [locale, setLocale] = useState<Locale>(DEFAULT_LOCALE);

  useEffect(() => {
    setLocale(getStoredLocale());
  }, []);

  const t = (key: string) => translate(locale, key);

  const {
    error,
    userId,
    userRoles,
    expectedOwner,
    expectedDev,
    expectedAdmin,
  } = searchParams;

  const features = [
    { icon: Server, label: t('login.featureServer') },
    { icon: CircleDollarSign, label: t('login.featureFinance') },
    { icon: CalendarDays, label: t('login.featureCalendar') },
    { icon: ShieldCheck, label: t('login.featureAudit') },
  ];

  const errorMessage = error
    ? t(`login.error.${error}`) !== `login.error.${error}`
      ? t(`login.error.${error}`)
      : t('login.errorDefault')
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-4xl grid md:grid-cols-2 rounded-3xl overflow-hidden glass-panel">
        <div className="hidden md:flex flex-col justify-between p-10 bg-gradient-to-br from-zinc-600/20 via-[#101010] to-zinc-600/10 border-r border-white/5">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img src="/logo.png" alt="4Kings" className="w-12 h-12 object-contain relative z-10" />
              <div className="absolute inset-0 bg-zinc-500/40 blur-xl rounded-full" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white leading-none">
                XG<span className="text-gradient">-HUB</span>
              </h1>
              <span className="text-[9px] text-zinc-500 font-bold tracking-[0.25em] uppercase">
                Command Center
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white leading-snug whitespace-pre-line">
              {t('login.tagline')}
            </h2>
            <div className="space-y-2.5">
              {features.map((f) => (
                <div key={f.label} className="flex items-center gap-3 text-sm text-[#c9c9c9]">
                  <div className="p-1.5 rounded-lg bg-white/[0.04] border border-white/5">
                    <f.icon className="w-4 h-4 text-zinc-400" />
                  </div>
                  {f.label}
                </div>
              ))}
            </div>
          </div>

          <p className="text-[10px] text-[#4a4a4a]">{t('login.copyright')}</p>
        </div>

        <div className="p-10 flex flex-col justify-center gap-6">
          <div>
            <h2 className="text-xl font-bold text-white">{t('login.title')}</h2>
            <p className="text-xs text-[#8a8a8a] mt-1">{t('login.subtitle')}</p>
          </div>

          {error && errorMessage && (
            <div className="space-y-4">
              <div className="text-xs text-zinc-400 bg-zinc-500/10 border border-zinc-500/20 rounded-xl p-3">
                {errorMessage}
              </div>

              {error === 'access_denied' && (
                <div className="text-[11px] bg-white/[0.02] border border-white/5 rounded-xl p-4 space-y-2.5 font-mono text-[#8a8a8a] leading-relaxed">
                  <p className="text-xs font-bold text-white uppercase tracking-wider font-sans mb-1">
                    {t('login.diagnosticsTitle')}
                  </p>
                  <div>
                    <span className="text-[#4a4a4a]">{t('login.discordUserId')}</span>{' '}
                    <span className="text-white font-bold select-all">{userId || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[#4a4a4a] block mb-1">{t('login.yourRoles')}</span>
                    <div className="flex flex-wrap gap-1">
                      {userRoles ? (
                        userRoles.split(',').map((r) => (
                          <span
                            key={r}
                            className="px-1.5 py-0.5 rounded bg-white/5 text-zinc-300 border border-white/5 select-all"
                          >
                            {r}
                          </span>
                        ))
                      ) : (
                        <span className="text-[#4a4a4a] italic">{t('login.noRoles')}</span>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-white/5 pt-2.5 space-y-1">
                    <p className="text-[10px] text-zinc-400 font-sans font-bold mb-1">
                      {t('login.envHint')}
                    </p>
                    <div className="flex justify-between">
                      <span className="text-[#4a4a4a]">DISCORD_ROLE_OWNER:</span>
                      <span className="text-zinc-400 select-all font-bold">{expectedOwner || t('login.notConfigured')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#4a4a4a]">DISCORD_ROLE_DEV:</span>
                      <span className="text-zinc-400 select-all font-bold">{expectedDev || t('login.notConfigured')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#4a4a4a]">DISCORD_ROLE_ADMIN:</span>
                      <span className="text-zinc-400 select-all font-bold">{expectedAdmin || t('login.notConfigured')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <a
            href="/api/auth/discord"
            className="w-full flex items-center justify-center gap-2.5 px-4 py-3.5 rounded-xl bg-white hover:bg-[#e6e6e6] text-[#0a0a0a] text-sm font-bold transition shadow-lg shadow-black/40"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
            {t('login.discordButton')}
          </a>
        </div>
      </div>
    </div>
  );
}
